import React, { useEffect, useMemo, useRef, useState } from "react";
import { BellRing, CheckCheck, ClipboardCheck, Package, RefreshCw, Store, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  FoodNotificationsService,
  isFoodPanelNotification,
  mapNotificationToRoute,
} from "../../../services/notifications.service";
import { connectSocket } from "../../../services/notificationsSocket";
import { getAuthUser } from "../../AdminAuth/components/authStore";

function timeAgo(value) {
  if (!value) return "";
  const date = new Date(value);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);

  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} day ago`;
}

function isUnread(item) {
  return !(item?.is_read || item?.isRead);
}

function getTitle(item) {
  return item?.title || item?.event_type || item?.type || "Notification";
}

function getMessage(item) {
  return item?.meta?.displayAction || item?.message || item?.body || item?.description || "Dashboard action updated.";
}

function getIcon(item = {}) {
  const text = `${item?.type || ""} ${item?.title || ""} ${item?.body || ""} ${item?.message || ""}`.toLowerCase();
  if (text.includes("order") || text.includes("driver") || text.includes("delivery")) return Truck;
  if (text.includes("approval") || text.includes("request")) return ClipboardCheck;
  if (text.includes("restaurant")) return Store;
  if (text.includes("food") || text.includes("combo") || text.includes("menu")) return Package;
  return BellRing;
}

export default function FoodNotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const navigate = useNavigate();
  const user = useMemo(() => getAuthUser() || {}, []);

  const unreadItems = useMemo(() => items.filter(isUnread), [items]);
  const unreadCount = unreadItems.length;
  const visibleItems = unreadItems.slice(0, 8);

  async function loadNotifications() {
    try {
      setLoading(true);
      const res = await FoodNotificationsService.list(user, { limit: 100, offset: 0 });
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load food notifications:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    const socket = connectSocket();
    if (!socket) return undefined;

    const handleNotification = (data) => {
      if (!isFoodPanelNotification(data)) return;
      setItems((prev) => [data, ...prev.filter((item) => item?.id !== data?.id)]);
    };

    socket.on("notification", handleNotification);
    return () => socket.off("notification", handleNotification);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleViewAll = () => {
    setOpen(false);
    navigate("/admin/food-dashboard/notifications");
  };

  async function handleOpenNotification(item) {
    const target = mapNotificationToRoute(item) || "/admin/food-dashboard/notifications";
    try {
      if (item?.id && isUnread(item)) {
        await FoodNotificationsService.markRead(item.id, user);
        setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, is_read: true, isRead: true } : n)));
      }
    } catch (err) {
      console.error("Failed to mark food notification read:", err);
    } finally {
      setOpen(false);
      navigate(target);
    }
  }

  async function handleMarkAllRead() {
    try {
      await FoodNotificationsService.markAllRead(user, items);
      setItems((prev) => prev.map((item) => ({ ...item, is_read: true, isRead: true })));
    } catch (err) {
      console.error("Failed to mark food notifications read:", err);
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((current) => !current);
          if (!open) loadNotifications();
        }}
        className="relative flex h-12 w-12 items-center justify-center rounded-[17px] border border-rose-200/80 bg-white/80 text-[#e04f68] shadow-[0_8px_22px_rgba(216,106,120,0.09)] ring-1 ring-white/70 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-[#fff7f8] hover:shadow-[0_12px_26px_rgba(216,106,120,0.15)] sm:h-[54px] sm:w-[54px]"
        aria-label="Notifications"
      >
        <BellRing size={18} />

        {unreadCount > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#d86a78] px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 top-[calc(100%+12px)] z-50 w-[370px] overflow-hidden rounded-[18px] border border-[#f2c6cc] bg-white shadow-[0_20px_55px_rgba(0,0,0,0.14)]">
          <div className="flex items-center justify-between border-b border-[#f2c6cc] bg-[#fff8f9] px-4 py-3">
            <div>
              <div className="text-[14px] font-bold text-black">Notifications</div>
              <div className="text-[11px] text-slate-400">
                {unreadCount > 0 ? `${unreadCount} unread food alert${unreadCount > 1 ? "s" : ""}` : "No unread food alerts"}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={loadNotifications}
                className="rounded-full border border-[#f2c6cc] bg-white p-2 text-[#d86a78] hover:bg-[#fff6f7]"
                title="Refresh"
              >
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              </button>
              <button
                type="button"
                onClick={handleMarkAllRead}
                disabled={unreadCount === 0}
                className="rounded-full border border-[#f2c6cc] bg-white p-2 text-[#d86a78] hover:bg-[#fff6f7] disabled:cursor-not-allowed disabled:opacity-50"
                title="Mark all read"
              >
                <CheckCheck size={14} />
              </button>
            </div>
          </div>

          <div className="max-h-[345px] overflow-y-auto p-2">
            {loading && !visibleItems.length ? (
              <div className="p-5 text-center text-[12px] text-slate-400">Loading food notifications...</div>
            ) : visibleItems.length ? (
              visibleItems.map((item) => {
                const Icon = getIcon(item);
                return (
                  <button
                    key={item.id || `${item.type}-${item.created_at || item.createdAt}`}
                    type="button"
                    onClick={() => handleOpenNotification(item)}
                    className="flex w-full gap-3 rounded-[14px] p-3 text-left transition hover:bg-[#fff6f7]"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-[#fff1f3] text-[#d86a78]">
                      <Icon size={18} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="truncate text-[13px] font-semibold text-black">{getTitle(item)}</div>
                        <span className="h-2 w-2 shrink-0 rounded-full bg-[#d86a78]" />
                      </div>

                      <div className="mt-1 line-clamp-2 text-[12px] text-slate-500">{getMessage(item)}</div>

                      <div className="mt-1 text-[11px] text-slate-400">
                        {timeAgo(item?.created_at || item?.createdAt)}
                      </div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="p-6 text-center text-[12px] text-slate-400">
                No unread food notifications.
              </div>
            )}
          </div>

          <div className="border-t border-[#f2c6cc] p-3">
            <button
              type="button"
              onClick={handleViewAll}
              className="w-full rounded-[12px] bg-[#d86a78] px-4 py-2.5 text-[12px] font-semibold text-white hover:bg-[#c95b69]"
            >
              View all notifications
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
